// FORGED Ed Systems — DEPLOY005 — v0.5.0 — intakeValidation.ts
// Last touched: 2026-05-20
//
// Per-step validation rules for the intake form. Each step's check returns
// either null (valid, can proceed) or a string error message to display.
// Server-side intake-save.js validates everything again before persisting.
//
// DEPLOY005 — Step 4 now validates target_code/target_process/target_position
// instead of the single bundled certification field.

import type { IntakeFormState } from "./intakeTypes";

export function validateStep1(state: IntakeFormState): string | null {
  if (!state.operating_mode) {
    return "Pick where you are in the term to continue.";
  }
  return null;
}

export function validateStep2(state: IntakeFormState): string | null {
  if (!state.evaluation_curriculum) {
    return "Pick a curriculum framework to continue.";
  }
  return null;
}

export function validateStep3(state: IntakeFormState): string | null {
  if (!state.instructor.background_discipline) {
    return "Pick the option that best describes your teaching background.";
  }
  if (state.equipment.booth_count < 1) {
    return "Booth count must be at least 1.";
  }
  if (state.equipment.machine_inventory.length === 0) {
    return "Add at least one welding machine.";
  }
  for (var i = 0; i < state.equipment.machine_inventory.length; i++) {
    var m = state.equipment.machine_inventory[i];
    if (m.count < 1) {
      return "Each welding machine entry must have a count of at least 1.";
    }
  }
  if (state.calendar.class_period_length_min < 1) {
    return "Class period length must be at least 1 minute.";
  }
  if (state.calendar.meetings_per_week < 1) {
    return "Class must meet at least once per week.";
  }
  if (state.calendar.total_term_weeks < 1) {
    return "Total term length must be at least 1 week.";
  }
  return null;
}

export function validateStep4(state: IntakeFormState): string | null {
  if (state.cohort.roster_size < 1) {
    return "Roster size must be at least 1.";
  }
  var exp = state.cohort.experience_levels;
  var sum = exp.beginner + exp.intermediate + exp.advanced;
  if (sum !== state.cohort.roster_size) {
    return (
      "Experience counts add to " +
      sum +
      " but your roster is " +
      state.cohort.roster_size +
      ". Adjust to match."
    );
  }
  if (!state.cohort.target_code) {
    return "Pick a target code, program, or standard.";
  }
  if (!state.cohort.target_process) {
    return "Pick a target welding process (or TBD).";
  }
  if (!state.cohort.target_position) {
    return "Pick a target welding position (or TBD).";
  }
  if (!state.cohort.target_date) {
    return "Pick a target certification date.";
  }
  var targetDate = new Date(state.cohort.target_date);
  if (isNaN(targetDate.getTime())) {
    return "That target date looks invalid. Pick another.";
  }
  if (targetDate < new Date()) {
    return "Target date is in the past. Pick a future date.";
  }
  return null;
}

export function validateStep5(state: IntakeFormState): string | null {
  if (!state.current_state) {
    return "Where the cohort is right now is required for this mode.";
  }
  if (state.current_state.weeks_remaining_in_term < 1) {
    return "Weeks remaining must be at least 1.";
  }
  if (state.current_state.weeks_remaining_in_term > state.calendar.total_term_weeks) {
    return "Weeks remaining cannot exceed your total term length.";
  }
  if (state.current_state.cohort_segments.length === 0) {
    return "Add at least one cohort segment.";
  }
  var totalStudents = 0;
  for (var i = 0; i < state.current_state.cohort_segments.length; i++) {
    var seg = state.current_state.cohort_segments[i];
    if (seg.student_count < 1) {
      return "Each segment must have at least 1 student.";
    }
    totalStudents += seg.student_count;
  }
  if (totalStudents !== state.cohort.roster_size) {
    return (
      "Segments sum to " +
      totalStudents +
      " but your roster is " +
      state.cohort.roster_size +
      ". Adjust counts so they match."
    );
  }
  return null;
}

export function validateStep6(_state: IntakeFormState): string | null {
  // Optional accordion — always valid.
  return null;
}

export function validateAll(state: IntakeFormState): string | null {
  var r = validateStep1(state);
  if (r) return r;
  r = validateStep2(state);
  if (r) return r;
  r = validateStep3(state);
  if (r) return r;
  r = validateStep4(state);
  if (r) return r;
  if (state.operating_mode === "mid_year" || state.operating_mode === "cert_prep_window") {
    r = validateStep5(state);
    if (r) return r;
  }
  return null;
}
